// Whitelist-based HTML sanitiser for article content.
//
// Runs on: pasted HTML (before insertion), on load into the editor (the value
// comes back from an API and is assigned into a live contentEditable DOM, so
// unsanitised markup would execute), and on save/preview.
//
// Approach: parse into a detached document, walk every element, drop anything
// not on the allow-list (unwrapping it — its children are kept, e.g. a pasted
// <span style="..."> becomes plain text rather than disappearing), strip every
// attribute except the few known-safe ones, and neutralise unsafe URL schemes.

const ALLOWED_TAGS = new Set([
  'p', 'h2', 'h3', 'strong', 'em', 'u', 's', 'mark', 'span', 'a', 'img',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'br', 'hr', 'div',
]);

// Marker element used for the "New Page" toolbar button — kept through
// sanitisation so BlogDetail can split an article into multiple pages on it.
const PAGE_BREAK_CLASS = 'rte-page-break';

const ALLOWED_SPAN_CLASSES = new Set([
  'rte-text-slate', 'rte-text-indigo', 'rte-text-teal',
  'rte-text-red', 'rte-text-amber', 'rte-text-green',
]);
const ALLOWED_MARK_CLASSES = new Set([
  'rte-hl-yellow', 'rte-hl-green', 'rte-hl-blue', 'rte-hl-pink',
]);

function isSafeUrl(url) {
  if (!url) return false;
  const trimmed = url.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return true;
  if (/^\//.test(trimmed)) return true; // relative/site-internal
  return false;
}

// Attributes are read before being stripped, then reapplied through the
// per-tag allow-list below — returns false when the element should be
// unwrapped instead (e.g. a link with an unsafe href).
function sanitizeElementAttributes(el, originalAttrs) {
  const tag = el.tagName.toLowerCase();

  if (tag === 'a') {
    const href = originalAttrs.href;
    if (isSafeUrl(href)) {
      el.setAttribute('href', href.trim());
      // External links open in a new tab safely; internal/relative ones don't need to.
      if (/^https?:/i.test(href.trim())) {
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer nofollow');
      }
    } else {
      return false; // unwrap — not a safe link
    }
  }

  if (tag === 'img') {
    const src = originalAttrs.src;
    if (!isSafeUrl(src) && !/^data:image\//i.test(src || '')) return false;
    el.setAttribute('src', src.trim());
    el.setAttribute('alt', originalAttrs.alt || '');
    el.setAttribute('loading', 'lazy');
  }

  if (tag === 'span') {
    const cls = (originalAttrs.class || '').split(/\s+/).filter((c) => ALLOWED_SPAN_CLASSES.has(c));
    if (cls.length) el.setAttribute('class', cls.join(' '));
    else if (!originalAttrs.class) return true; // plain span from paste, keep as neutral wrapper
    else return false; // unknown span class — unwrap
  }

  if (tag === 'mark') {
    const cls = (originalAttrs.class || '').split(/\s+/).filter((c) => ALLOWED_MARK_CLASSES.has(c));
    if (cls.length) el.setAttribute('class', cls.join(' '));
  }

  if (tag === 'div') {
    // Only the page-break marker div is allowed through; any other div is
    // unwrapped (its content is kept, just not the wrapping div itself).
    if ((originalAttrs.class || '').split(/\s+/).includes(PAGE_BREAK_CLASS)) {
      el.setAttribute('class', PAGE_BREAK_CLASS);
      el.setAttribute('contenteditable', 'false');
    } else {
      return false;
    }
  }

  return true;
}

function walk(node) {
  // Snapshot children first — cleanElement / unwrapping mutates the tree
  // while we're iterating, so a live NodeList would skip/duplicate nodes.
  const children = Array.from(node.children || []);
  children.forEach((child) => {
    const tag = child.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
      const parent = child.parentNode;
      // Recurse into the child's own children before unwrapping so nested
      // disallowed tags inside it are cleaned too.
      walk(child);
      while (child.firstChild) parent.insertBefore(child.firstChild, child);
      parent.removeChild(child);
      return;
    }

    const originalAttrs = {};
    Array.from(child.attributes).forEach((a) => { originalAttrs[a.name] = a.value; });
    Array.from(child.attributes).forEach((a) => child.removeAttribute(a.name));

    const keep = sanitizeElementAttributes(child, originalAttrs);
    if (keep === false) {
      const parent = child.parentNode;
      walk(child);
      while (child.firstChild) parent.insertBefore(child.firstChild, child);
      parent.removeChild(child);
      return;
    }

    walk(child);
  });
}

export function sanitizeHtml(html) {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Remove script/style/iframe/object entirely — walk() unwraps unknown
    // tags (keeping their text), which is wrong for these; their content
    // must go too.
    doc.querySelectorAll('script, style, iframe, object, embed, form, input, button').forEach((n) => n.remove());
    walk(doc.body);
    return doc.body.innerHTML.trim();
  } catch {
    return '';
  }
}

export function htmlToText(html) {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  } catch {
    return '';
  }
}

export const PAGE_BREAK_HTML = `<div class="${PAGE_BREAK_CLASS}" contenteditable="false"></div>`;
export const PAGE_BREAK_SELECTOR = `.${PAGE_BREAK_CLASS}`;
